/**
 * The sheet footer for a reader who is not on the ticket's Team, at an act
 * only its members may take (`sheetFooter`'s `not_member`): who can, by face
 * and name, and a way in. "Ask Jack to add you" posts a comment on the ticket
 * that @-mentions the Team's owner, which the comments route turns into a
 * notification for them.
 */
import { useCallback, useEffect, useState } from "react";

import { FaceStack } from "../teams/people/Face.js";
import {
  askToAddSourceComment,
  askToJoinComment,
  namesPhrase,
  ownerFirst,
  personFirstName,
  personName,
} from "../teams/people/people.js";
import "../teams/people/people.css";
import { SOURCE_SHAPE_COPY, sourceShapeFor } from "../teams/people/sourceShape.js";
import { fetchTeamMembers, type TeamMember } from "../teams/teamsApi.js";
import { captureUiError } from "../sentry.js";
import { toast } from "../ui/toast.js";

/** The Team's people, read only while `enabled` (the reader is not on it). */
export function useTeamPeopleFor(
  deskId: string | null,
  enabled: boolean,
): { members: TeamMember[] | null; failed: boolean } {
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!enabled || !deskId) return;
    let cancelled = false;
    fetchTeamMembers(deskId)
      .then((body) => {
        if (!cancelled) setMembers(ownerFirst(body.members));
      })
      .catch((err: unknown) => {
        // The footer still says the reader is not on the Team, and that it
        // could not say who is.
        if (!cancelled) setFailed(true);
        captureUiError(err, {
          area: "Work",
          operation: "sheet_read_team_people",
          deskId,
          severity: "warning",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [deskId, enabled]);
  return { members, failed };
}

/** "**You're not on Checkout.** You can read and comment. Maya, Jack and 2 others can start the plan." */
export function NotMemberWhy({
  teamName,
  members,
  memberVerb,
  failed = false,
}: {
  teamName: string;
  members: readonly TeamMember[] | null;
  memberVerb: string;
  /** The Team's people could not be read: say so, rather than read as nobody. */
  failed?: boolean;
}) {
  const people = members ?? [];
  const names = people.map((member) => personFirstName(member));
  const faces = people.map((member) => ({
    userId: member.userId,
    name: personName(member),
    avatarUrl: member.avatarUrl,
  }));
  return (
    <span className="notmember" data-testid="sheet-not-member">
      {faces.length > 0 && <FaceStack people={faces} max={3} size={20} />}
      <span>
        <b>You&apos;re not on {teamName || "this Team"}.</b> You can read and comment.{" "}
        {namesPhrase(names)} can {memberVerb}.{failed && " Could not read who is on this Team."}
      </span>
    </span>
  );
}

/** The secondary verb: post the ask as a comment, once. */
export function useAskToJoin(input: {
  workItemId: string;
  teamName: string;
  members: readonly TeamMember[] | null;
  memberVerb: string;
}) {
  const { workItemId, teamName, members, memberVerb } = input;
  const owner = members?.find((member) => member.role === "owner") ?? null;
  const [state, setState] = useState<"idle" | "sending" | "asked">("idle");
  const label =
    state === "asked"
      ? "Asked"
      : owner
        ? `Ask ${personFirstName(owner)} to add you`
        : "Ask to be added";

  const ask = useCallback(async () => {
    if (state !== "idle") return;
    setState("sending");
    try {
      const res = await fetch(`/api/work-items/${encodeURIComponent(workItemId)}/comments`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ body: askToJoinComment({ owner, teamName, memberVerb }) }),
      });
      if (!res.ok) throw new Error(`comment failed (${res.status})`);
      setState("asked");
      toast.success(
        owner
          ? `Asked ${personFirstName(owner)}. It is in the ticket's comments.`
          : "Asked. It is in the ticket's comments.",
      );
    } catch (err) {
      captureUiError(err, { area: "Work", operation: "sheet_ask_to_join", workItemId });
      setState("idle");
      toast.error("Could not post the ask. Try again, or comment on the ticket.");
    }
  }, [state, workItemId, owner, teamName, memberVerb]);

  return { label, ask, disabled: state !== "idle" };
}

/**
 * A plan question's "Ask Priya to add Database to this Team", for a reader who
 * cannot change the Team's connected tools: the same comment-and-mention path
 * as `useAskToJoin`, one ask per source (a plan can name two).
 */
export function useAskToAddSource(input: {
  workItemId: string;
  teamName: string;
  members: readonly TeamMember[] | null;
  askerName: string;
  /** The sources hidden from the Team (YAG-986): the ask is to show one; any other source is to connect. */
  hiddenSources?: readonly string[];
}) {
  const { workItemId, teamName, members, askerName } = input;
  const hiddenSources = input.hiddenSources;
  const owner = members?.find((member) => member.role === "owner") ?? null;
  const modeFor = (source: string) => sourceShapeFor(hiddenSources, source);
  const [states, setStates] = useState<Record<string, "sending" | "asked">>({});
  const setState = (source: string, state: "sending" | "asked" | null) =>
    setStates((prev) => {
      const next = { ...prev };
      if (state) next[source] = state;
      else delete next[source];
      return next;
    });

  const labelFor = (sourceLabel: string, source: string) => {
    if (states[source] === "asked") return "Asked";
    const what = SOURCE_SHAPE_COPY[modeFor(source)].ask(sourceLabel);
    return owner ? `Ask ${personFirstName(owner)} to ${what}` : `Ask to ${what}`;
  };

  const ask = useCallback(
    async (source: string, sourceLabel: string) => {
      if (states[source]) return;
      setState(source, "sending");
      try {
        const res = await fetch(`/api/work-items/${encodeURIComponent(workItemId)}/comments`, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            body: askToAddSourceComment({
              owner,
              teamName,
              source: sourceLabel,
              askerName,
              mode: modeFor(source),
            }),
          }),
        });
        if (!res.ok) throw new Error(`comment failed (${res.status})`);
        setState(source, "asked");
        toast.success(
          owner
            ? `Asked ${personFirstName(owner)}. It is in the ticket's comments.`
            : "Asked. It is in the ticket's comments.",
        );
      } catch (err) {
        captureUiError(err, {
          area: "Work",
          operation: "sheet_ask_to_add_source",
          workItemId,
          source,
        });
        setState(source, null);
        toast.error("Could not post the ask. Try again, or comment on the ticket.");
      }
    },
    [states, workItemId, owner, teamName, askerName, hiddenSources],
  );

  return { labelFor, ask, disabledFor: (source: string) => Boolean(states[source]) };
}
